import { type ReactNode } from 'react'

import {
  parseMarkdownText,
  type TemplateSlotLabel,
  type WhatsAppSpan,
} from '@aeris/communication'

/**
 * Uma árvore de spans virando elementos React — o único lugar que sabe fazer
 * isso (F083).
 *
 * ## Por que é um lugar só
 *
 * `dangerouslySetInnerHTML` é proibido pelo CLAUDE.md, e o motivo aqui é
 * concreto: o texto vem do cliente. Um segundo renderizador significaria uma
 * segunda oportunidade de alguém "simplificar" com uma string de HTML.
 *
 * Nasceu dentro de `whatsapp-preview.tsx`, para a prévia de um modelo. A F083
 * o trouxe para fora porque o INBOX precisa do mesmo trabalho: as mensagens
 * são guardadas em Markdown, e a tela mostrava a marcação crua — o atendente
 * lia `**Thiago:**` enquanto o cliente via negrito.
 *
 * A prévia e a bolha renderizam a MESMA árvore. Duas aparências para o mesmo
 * dado seria a discordância que a doutrina de `receipt-dialog.tsx` existe para
 * impedir.
 */

const spanTitle = (
  index: number,
  slotLabels: readonly TemplateSlotLabel[] | undefined
): string => {
  const label = slotLabels?.find((slot) => slot.index === index)
  return label?.name ?? `Variável ${String(index)} sem nome`
}

export const renderSpans = (
  spans: readonly WhatsAppSpan[],
  slotLabels: readonly TemplateSlotLabel[] | undefined,
  keyPrefix: string
): ReactNode =>
  spans.map((span, position) => {
    const key = `${keyPrefix}-${String(position)}`
    switch (span.kind) {
      case 'text':
        return <span key={key}>{span.text}</span>
      case 'bold':
        return (
          <strong key={key} className="font-(--weight-semibold)">
            {renderSpans(span.spans, slotLabels, key)}
          </strong>
        )
      case 'italic':
        return <em key={key}>{renderSpans(span.spans, slotLabels, key)}</em>
      case 'strike':
        return <s key={key}>{renderSpans(span.spans, slotLabels, key)}</s>
      case 'mono':
        return (
          <code key={key} className="font-(--font-mono)">
            {renderSpans(span.spans, slotLabels, key)}
          </code>
        )
      case 'code':
        return (
          <code key={key} className="font-(--font-mono)">
            {span.text}
          </code>
        )
      case 'placeholder':
        return (
          <span
            key={key}
            title={spanTitle(span.index, slotLabels)}
            className="rounded-(--radius-sm) bg-(--warning-subtle) px-1 py-0.5 font-(--weight-medium)"
          >
            {`{{${String(span.index)}}}`}
          </span>
        )
    }
  })

/**
 * O corpo de uma mensagem, com a formatação que o cliente vê.
 *
 * `parseMarkdownText` e não `parseWhatsAppText`: o corpo guardado está em
 * Markdown — é assim que o gateway o entrega e é assim que ele o espera de
 * volta. Ler com a gramática errada inverteria negrito e itálico, que é o erro
 * que a F080 corrigiu do outro lado.
 *
 * `whitespace-pre-wrap` fica em quem chama, junto do resto do estilo da bolha:
 * a quebra de linha é o prefixo `**Nome:**\\n` da resposta, e ela pertence ao
 * desenho da bolha, não ao texto.
 */
export const MessageText = ({ body }: { body: string }): ReactNode =>
  renderSpans(parseMarkdownText(body), undefined, 'msg')
